import { client } from '../Client'

// Per onderzoek is de afgeleide set (ingevulde momenten, anonieme momenten,
// tussentijd) voor élke deelnemer dezelfde. Zonder deze cache haalt elke
// detailpagina hem opnieuw op, en sinds 'Geldt voor alle huishoudens' bereikt
// één onderzoek alle huishoudens: ~12k documenten per keer dat je een
// huishouden opent. Zie docs/inbox.md #246.
//
// BEWUST KORT. De hook luistert niet (geen onSnapshot); verversen gebeurde tot
// nu toe door opnieuw te mounten. Met een lange bewaartijd zou een zojuist
// ingevulde vragenlijst minutenlang niet zichtbaar worden. Eén minuut haalt de
// herhaling eruit bij het doorklikken — de enige plek waar het pijn deed —
// zonder dat gedrag te breken.

/** Hoe lang een afgeleide set geldig blijft, in milliseconden. */
const TTL_MS = 60 * 1000

/** @type {Map<string, {at: number, value: *}>} */
const cache = new Map()

/** @type {Map<string, Promise<*>>} */
const onderweg = new Map()

/** Per sleutel: hoe vaak hij geleegd is. Een ophaalactie bewaart zijn uitkomst
 *  alleen als er sinds zijn start niet geleegd is — anders draagt hij de stand
 *  van vóór het koppelen (/complete 2026-10-14). @type {Map<string, number>} */
const geleegd = new Map()

/** Tenant in de sleutel: anders lekt de set naar een andere organisatie zodra
 *  iemand van omgeving wisselt zonder de pagina te herladen. */
const sleutelVoor = (researchId) => `${client}|${researchId}`

/**
 * De afgeleide set van één onderzoek, uit de cache of vers opgehaald.
 *
 * Een mislukking wordt NIET bewaard — niet de `null` die een geweigerde read
 * teruggeeft en niet een gegooide fout. Anders staat het gat een minuut lang
 * vast, en een leeg antwoord leest op het scherm als 'niemand heeft ingevuld'.
 * AI-CONTEXT: See docs/pitfalls.md#api-fallback-collapses-failure-into-empty
 *
 * @template T
 * @param {string} researchId - `Research.ID`
 * @param {(researchId: string) => Promise<T>} fetcher - Haalt de set op bij een misser
 * @returns {Promise<T>}
 */
const getResearchAggregate = async (researchId, fetcher) => {
    const sleutel = sleutelVoor(researchId)

    const bewaard = cache.get(sleutel)
    if (bewaard && Date.now() - bewaard.at < TTL_MS) return bewaard.value

    const lopend = onderweg.get(sleutel)
    if (lopend) return lopend

    const ronde = geleegd.get(sleutel) || 0
    const belofte = (async () => {
        const waarde = await fetcher(researchId)
        const nogActueel = (geleegd.get(sleutel) || 0) === ronde
        if (nogActueel && waarde !== null && waarde !== undefined) cache.set(sleutel, { at: Date.now(), value: waarde })
        return waarde
    })()

    onderweg.set(sleutel, belofte)
    try {
        return await belofte
    } finally {
        // Alleen de eigen: na een legen kan er al een nieuwe onderweg zijn.
        if (onderweg.get(sleutel) === belofte) onderweg.delete(sleutel)
    }
}

/**
 * Haalt één onderzoek uit de cache, zodat de volgende lezer verse antwoorden
 * krijgt. Nodig na een handeling die antwoorden aan iemand koppelt (de kaarten
 * 'Deelnemers om te koppelen' en 'Adressen om te koppelen'): anders toont de
 * pagina die je daarna opent een minuut lang de stand van vóór het koppelen
 * (testronde 2026-10-13, B13).
 *
 * @param {string} [researchId] - `Research.ID`; leeg doet niets
 */
const invalidateResearchAggregate = (researchId) => {
    if (!researchId) return
    const sleutel = sleutelVoor(researchId)
    cache.delete(sleutel)
    // Een lopende ophaalactie draagt de oude stand: niet meer op wachten, en
    // zijn uitkomst niet meer bewaren.
    onderweg.delete(sleutel)
    geleegd.set(sleutel, (geleegd.get(sleutel) || 0) + 1)
}

/** Leegt de cache. Alleen voor tests — in de app verloopt hij vanzelf. */
const clearResearchAggregateCache = () => {
    cache.clear()
    onderweg.clear()
    geleegd.clear()
}

export default getResearchAggregate
export { getResearchAggregate, clearResearchAggregateCache, invalidateResearchAggregate, TTL_MS }
